import { resolveLocalizedEntityName } from '../../constants/commerceNameLocales';

/** Meta / catalog: inventory status value for "no stock" */
export function isCommerceOutOfStock(availability) {
  if (availability == null) return false;
  const s = String(availability).trim().toLowerCase().replace(/\s+/g, ' ');
  return s === 'out of stock';
}

/** Catalog listing status (commerce_products.status); empty / missing treated as orderable for status. */
export function isCatalogStatusActive(status) {
  if (status == null) return true;
  const s = String(status).trim();
  if (s === '') return true;
  return s.toLowerCase() === 'active';
}

/** 目錄狀態為 "out of stock"（WPA 顯示斷貨）—與庫存「沒有庫存」並列時只保留目錄一行。 */
export function isCatalogOutOfStockStatus(status) {
  if (status == null) return false;
  return String(status).trim().toLowerCase() === 'out of stock';
}

/** status 為「停止訂購」—推薦／所有商品清單不顯示該筆（與 API 邏輯一致）。 */
export function isWpaListHiddenStopOrderingStatus(status) {
  if (status == null) return false;
  const t = String(status).trim();
  if (t === '停止訂購' || t === '停止订购') return true;
  return t.toLowerCase() === 'stop ordering';
}

/** WPA：不可下單＝沒有庫存，或商品狀態非 active */
export function isWpaProductOrderable(product) {
  if (!product) return false;
  if (isCommerceOutOfStock(product.availability)) return false;
  if (!isCatalogStatusActive(product.status)) return false;
  return true;
}

/** 目錄狀態（commerce_products.status）已知英文值 → i18n；其餘原樣顯示 */
const WPA_CATALOG_STATUS_I18N_KEYS = [
  ['out of stock', 'wpa.common.catalogStatusLabels.outOfStock'],
  ['stop ordering', 'wpa.common.catalogStatusLabels.stopOrdering'],
  ['suspend', 'wpa.common.catalogStatusLabels.suspend'],
];

export function formatWpaCatalogStatusLabel(raw, t) {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  for (const [needle, key] of WPA_CATALOG_STATUS_I18N_KEYS) {
    if (lower === needle) return t(key);
  }
  return trimmed;
}

function resolveProductUnitField(product, primaryKeys, localesKeys, uiLocale) {
  if (!product) return '';
  const primary = product[primaryKeys[0]] ?? product[primaryKeys[1]];
  const localesRaw = product[localesKeys[0]] ?? product[localesKeys[1]];
  if (uiLocale) {
    const resolved = resolveLocalizedEntityName(primary, localesRaw, uiLocale);
    if (resolved) return resolved;
  }
  if (primary == null || primary === '') return '';
  return String(primary).trim();
}

/** 數量單位（整箱／整包等）；uiLocale 與登入語系一致時依 unitOfMeasureLocales 解析 */
export function getProductUnitOfMeasure(product, uiLocale) {
  return resolveProductUnitField(
    product,
    ['unitOfMeasure', 'unit_of_measure'],
    ['unitOfMeasureLocales', 'unit_of_measure_locales'],
    uiLocale,
  );
}

/** 散裝單位（加減數量列）；uiLocale 與登入語系一致時依 unitOfBrokenPackLocales 解析 */
export function getProductUnitOfBrokenPack(product, uiLocale) {
  return resolveProductUnitField(
    product,
    ['unitOfBrokenPack', 'unit_of_brokenpack'],
    ['unitOfBrokenPackLocales', 'unit_of_brokenpack_locales'],
    uiLocale,
  );
}

/** 加減數量列中央單位：散裝單位優先，無則數量單位，最後 fallback */
export function getProductQtyStepUnitLabel(product, uiLocale, fallback = '') {
  const broken = getProductUnitOfBrokenPack(product, uiLocale);
  if (broken) return broken;
  const measure = getProductUnitOfMeasure(product, uiLocale);
  if (measure) return measure;
  return fallback;
}

/** 每「散裝單位」單價；需 qtyPerUnit（與 CommerceBrokenPackPricing 一致）。 */
export function getProductBrokenPackUnitPrice(product) {
  if (!product || product.showPrice === false || product.price == null) return null;
  const p = Number(product.price);
  if (!Number.isFinite(p)) return null;
  const u = parsePositiveQtyPerUnit(product);
  if (u == null) return null;
  return computeBrokenPackLineAmount(1, p, u, null);
}

/**
 * WPA 加購 modal 標題下價錢列：「每{數量單位}」「每{散裝單位}」（依登入語系）。
 * @param {(key: string, params?: Record<string, string | number>) => string} t
 * @returns {string[]}
 */
export function buildWpaPerUnitPriceLines(product, uiLocale, t) {
  if (!product) return [];
  if (product.showPrice === false || product.price == null) {
    return [t('wpa.common.priceInquire')];
  }
  const packPrice = Number(product.price);
  if (!Number.isFinite(packPrice)) return [t('wpa.common.priceInquire')];

  const fmtMoney = (n) => `$${Number(n).toFixed(2)}`;
  const measureLabel = getProductUnitOfMeasure(product, uiLocale) || t('wpa.addToCart.defaultUnit');
  const lines = [
    t('wpa.addToCart.perQtyFmt', { unit: measureLabel, price: fmtMoney(packPrice) }),
  ];

  const brokenLabel = getProductUnitOfBrokenPack(product, uiLocale);
  const brokenUnitPrice = getProductBrokenPackUnitPrice(product);
  if (brokenLabel && brokenUnitPrice != null) {
    const sameLabel = brokenLabel.trim().toLowerCase() === measureLabel.trim().toLowerCase();
    const samePrice = Math.abs(brokenUnitPrice - packPrice) < 0.005;
    if (!sameLabel || !samePrice) {
      lines.push(t('wpa.addToCart.perQtyFmt', { unit: brokenLabel, price: fmtMoney(brokenUnitPrice) }));
    }
  }

  return lines;
}

/** WPA API：minOrderQuantity（>0）才視為有 MOQ；支援小數。 */
export function parsePositiveMoq(product) {
  if (!product) return null;
  const v = product.minOrderQuantity ?? product.min_order_quantity;
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

const WPA_MOQ_QTY_EPS = 1e-9;

function parseLineQuantityForMoqCheck(line) {
  if (!line) return 0;
  const v = line.quantity;
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/** 品項顯示名稱（MOQ 提示用）。 */
export function lineLabelForMoq(line) {
  if (!line) return '';
  const name = String(line.name || line.productName || '').trim();
  if (name) return name;
  return String(line.itemNo || '').trim();
}

function formatQtyForMoqMessage(n) {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  const rounded = Number(n.toFixed(6));
  const s = String(rounded);
  if (s.includes('.')) return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return s;
}

/**
 * 收集未達 MOQ 的明細（quantity 小於 minOrderQuantity）。
 * @returns {{ label: string, moq: number, quantity: number }[]}
 */
export function collectMoqViolations(lines) {
  if (!Array.isArray(lines)) return [];
  const out = [];
  for (const line of lines) {
    const moq = parsePositiveMoq(line);
    if (moq == null) continue;
    const q = parseLineQuantityForMoqCheck(line);
    if (q + WPA_MOQ_QTY_EPS < moq) {
      out.push({ label: lineLabelForMoq(line), moq, quantity: q });
    }
  }
  return out;
}

/**
 * 組合「不可提交」的 MOQ 說明（多語系 t）。
 * @param {(key: string, params?: Record<string, string | number>) => string} t
 */
export function buildMoqSubmitErrorMessage(violations, t) {
  if (!violations.length) return null;
  const lineMsgs = violations.map((v) =>
    t('wpa.common.moqSubmitLineFmt', {
      label: v.label || t('wpa.common.emDash'),
      moq: formatQtyForMoqMessage(v.moq),
      qty: formatQtyForMoqMessage(v.quantity),
    })
  );
  return `${t('wpa.common.moqSubmitBlocked')}\n\n${lineMsgs.join('\n')}`;
}

/**
 * 快速加減列步進用：主檔 `oqm`（字串可存數字）；無法解析為正數則視為未設定。
 * 與後台「OQM」欄位同源；非數字內容時回傳 null。
 */
export function parsePositiveOqm(productOrLine) {
  if (!productOrLine) return null;
  const v = productOrLine.oqm ?? productOrLine.Oqm;
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * 是否鎖定「每次 ±1」按鈕。
 * 規則：只看 OQM（與 MOQ 無關，MOQ 由提交時驗證）。
 *   - OQM > 0：鎖 ±1，須按 OQM 步進（OQM = 1 時 ±1 與 ±OQM 等價，仍可用快捷列）。
 *   - OQM 為空：放行 ±1，快捷列改用每單位數量 qtyPerUnit。
 */
export function shouldLockWpaUnitStepOfOne(productOrLine) {
  return parsePositiveOqm(productOrLine) != null;
}

/** 主檔「每單位數量」（>0）— 僅用於無 MOQ 時的散裝拆算／大步。 */
export function parsePositiveQtyPerUnit(productOrLine) {
  if (!productOrLine) return null;
  const v = productOrLine.qtyPerUnit ?? productOrLine.qty_per_unit;
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * 快速加減列（-N / +N）的步進值：
 *   - OQM 有值（> 0）：使用 OQM（此時 ±1 會被鎖死，買家須以 OQM 步進）。
 *   - OQM 空、qtyPerUnit > 1：使用 qtyPerUnit（±1 仍可用做微調）。
 *   - qtyPerUnit = 1（與上方 ±1 等價，無意義）或無設定：回 null，由呼叫端套預設值（目前統一為 10）。
 */
export function resolveFastQtyStepSize(productOrLine) {
  const oqm = parsePositiveOqm(productOrLine);
  if (oqm != null) return oqm;
  const qpu = parsePositiveQtyPerUnit(productOrLine);
  if (qpu != null && qpu > 1) return qpu;
  return null;
}

/** 顯示於加減步進按鈕標籤（整數不帶小數；小數最多 4 位並去尾 0）。 */
export function formatQtyStepForDisplay(n) {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  const t = Number(n.toFixed(4));
  return String(t);
}

/**
 * 明細金額（與後端 CommerceBrokenPackPricing 一致）。
 * 只要 qtyPerUnit（>0）有設定就依散裝件數拆算：(full/u)*price + remainder*(price/u)；否則 quantity×unitPrice。
 * MOQ（最小訂量）僅做步進驗證用，不影響金額公式。
 */
export function computeBrokenPackLineAmount(quantity, unitPrice, qtyPerUnit, minOrderQuantity) {
  const q = Number(quantity);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return 0;
  const uRaw = qtyPerUnit != null && qtyPerUnit !== '' ? Number(qtyPerUnit) : NaN;
  if (!Number.isFinite(uRaw) || uRaw <= 0) {
    return roundWpaMoneyAmount(q * p);
  }
  const u = uRaw;
  const remainder = ((q % u) + u) % u;
  const full = q - remainder;
  const raw = (full / u) * p + remainder * (p / u);
  return roundWpaMoneyAmount(raw);
}

/** 由明細／商品物件取欄位後計算金額。 */
export function computeLineAmountFromRow(quantity, unitPrice, row) {
  if (!row) return computeBrokenPackLineAmount(quantity, unitPrice, null, null);
  return computeBrokenPackLineAmount(
    quantity,
    unitPrice,
    row.qtyPerUnit ?? row.qty_per_unit,
    row.minOrderQuantity ?? row.min_order_quantity,
  );
}

function roundWpaMoneyAmount(v) {
  if (!Number.isFinite(v)) return 0;
  const scaled = Math.round(Math.abs(v) * 100) / 100;
  return v < 0 ? -scaled : scaled;
}

/** WPA 商品列表排序：itemNo ASC（對應 commerce_products.product_retailer_id）。 */
export function sortWpaProducts(products) {
  if (!Array.isArray(products)) return [];
  return [...products].sort((a, b) => {
    const itemA = String(a?.itemNo ?? '').trim();
    const itemB = String(b?.itemNo ?? '').trim();
    if (itemA !== itemB) {
      return itemA.localeCompare(itemB, undefined, { numeric: true, sensitivity: 'base' });
    }
    const idA = String(a?.id ?? '');
    const idB = String(b?.id ?? '');
    return idA.localeCompare(idB);
  });
}
